import L from "leaflet";
import type { SiteSeverity } from "@safers/api";
import { MARKER_SLIDE_MS, slideEase, type ModelDevicePoint } from "@safers/model/devices";
import {
  createDevicePin,
  DEVICE_PIN_BOX,
  updateDevicePin,
  type DevicePin,
} from "@safers/model/pin";
import { SITE_Z } from "./zoomFade";
import { createClusterHtml, worstSeverity } from "./markers";

const DEVICE_LABEL_ZOOM = SITE_Z + 1;
const DEVICE_CLUSTER_PX = 50;
const DEVICE_CLUSTER_OFF_ZOOM = SITE_Z + 2;
const CLUSTER_SIZE = 38;

const SPREAD_MS = 320;
const SPREAD_EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)";
const POP_MS = 240;
const POP_EASE = "cubic-bezier(0.34, 1.56, 0.64, 1)";
const PIN_REST = "translate(-50%, -50%)";

interface DeviceMarker {
  marker: L.Marker;
  pin: DevicePin;
  target: L.LatLngLiteral;
  slide?: number;
  spread?: Animation;
}

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function pop(element: Element | null | undefined, rest: string, origin: string) {
  element?.animate(
    [
      { transform: `${rest} scale(0.6)`, opacity: 0, transformOrigin: origin },
      { transform: `${rest} scale(1)`, opacity: 1, transformOrigin: origin },
    ],
    { duration: POP_MS, easing: POP_EASE }
  );
}

function offsetFrom(map: L.Map, entry: DeviceMarker, from: L.LatLng) {
  return map.latLngToLayerPoint(from).subtract(map.latLngToLayerPoint(entry.marker.getLatLng()));
}

function spreadOut(map: L.Map, entry: DeviceMarker, from: L.LatLng) {
  const { x, y } = offsetFrom(map, entry, from);
  entry.spread = entry.pin.button.animate(
    [
      { transform: `${PIN_REST} translate(${x}px, ${y}px) scale(0.4)`, opacity: 0 },
      { transform: PIN_REST, opacity: 1 },
    ],
    { duration: SPREAD_MS, easing: SPREAD_EASE }
  );
}

function gatherIn(map: L.Map, entry: DeviceMarker, to: L.LatLng, done: () => void) {
  const { x, y } = offsetFrom(map, entry, to);
  const animation = entry.pin.button.animate(
    [
      { transform: PIN_REST, opacity: 1 },
      { transform: `${PIN_REST} translate(${x}px, ${y}px) scale(0.4)`, opacity: 0 },
    ],
    { duration: SPREAD_MS, easing: SPREAD_EASE, fill: "forwards" }
  );
  animation.onfinish = () => {
    done();
    animation.cancel();
  };
  entry.spread = animation;
}

function slideMarker(entry: DeviceMarker, to: L.LatLngLiteral) {
  if (entry.slide) cancelAnimationFrame(entry.slide);
  const from = entry.marker.getLatLng();
  const start = performance.now();
  const step = (now: number) => {
    const t = Math.min(1, (now - start) / MARKER_SLIDE_MS);
    const k = slideEase(t);
    entry.marker.setLatLng([
      from.lat + (to.lat - from.lat) * k,
      from.lng + (to.lng - from.lng) * k,
    ]);
    entry.slide = t < 1 ? requestAnimationFrame(step) : undefined;
  };
  entry.slide = requestAnimationFrame(step);
}

function stopSlide(entry: DeviceMarker) {
  entry.spread?.cancel();
  if (entry.slide) cancelAnimationFrame(entry.slide);
  entry.slide = undefined;
}

function deviceIcon(pin: DevicePin): L.DivIcon {
  return L.divIcon({
    html: pin.button,
    className: "safers-poi-icon safers-device-icon",
    iconSize: [DEVICE_PIN_BOX.width, DEVICE_PIN_BOX.height],
    iconAnchor: [DEVICE_PIN_BOX.anchorX, DEVICE_PIN_BOX.anchorY],
  });
}

function groupNearby(map: L.Map, devices: ModelDevicePoint[], distancePx: number) {
  const groups = devices.map((device) => {
    const point = map.latLngToLayerPoint([device.lat, device.lon]);
    return { members: [device], x: point.x, y: point.y };
  });
  for (let merged = true; merged; ) {
    merged = false;
    for (let i = 0; i < groups.length && !merged; i += 1) {
      for (let j = i + 1; j < groups.length; j += 1) {
        const a = groups[i]!;
        const b = groups[j]!;
        if (Math.hypot(a.x - b.x, a.y - b.y) > distancePx) continue;
        const total = a.members.length + b.members.length;
        a.x = (a.x * a.members.length + b.x * b.members.length) / total;
        a.y = (a.y * a.members.length + b.y * b.members.length) / total;
        a.members.push(...b.members);
        groups.splice(j, 1);
        merged = true;
        break;
      }
    }
  }
  return groups.map((group) => ({
    members: group.members,
    center: map.layerPointToLatLng([group.x, group.y]),
  }));
}

const clusterSeverity = (device: ModelDevicePoint): SiteSeverity =>
  device.status === "OFFLINE" ? worstSeverity([device.severity, "UNCERTAIN"]) : device.severity;

/**
 * 2D 지도의 장비 핀 레이어.
 * 팝업·선택 상태는 지도 컴포넌트가 들고, 여기서는 핀과 묶음만 그린다.
 */
export function createDeviceMarkerLayer(
  map: L.Map,
  onSelect: (id: string, anchor: HTMLButtonElement) => void
) {
  const layer = L.layerGroup().addTo(map);
  const clusterLayer = L.layerGroup().addTo(map);
  const markers = new Map<string, DeviceMarker>();
  let devices: ModelDevicePoint[] = [];
  const labelDetail = () => String(map.getZoom() >= DEVICE_LABEL_ZOOM);

  let groupedAt = new Map<string, L.LatLng>();
  let clusterKeys = new Set<string>();
  let revealed = false;

  const regroup = () => {
    const animate = !reducedMotion();
    clusterLayer.clearLayers();
    const nextGroupedAt = new Map<string, L.LatLng>();
    const nextKeys = new Set<string>();
    if (map.getZoom() < DEVICE_CLUSTER_OFF_ZOOM) {
      for (const { members, center } of groupNearby(map, devices, DEVICE_CLUSTER_PX)) {
        if (members.length < 2) continue;
        const key = members
          .map((member) => member.deviceId)
          .sort()
          .join("|");
        nextKeys.add(key);
        const gathering = members.some(
          (member) =>
            !groupedAt.has(member.deviceId) && layer.hasLayer(markers.get(member.deviceId)!.marker)
        );
        for (const member of members) nextGroupedAt.set(member.deviceId, center);
        const bounds = L.latLngBounds(members.map((member) => [member.lat, member.lon]));
        const severity = worstSeverity(members.map(clusterSeverity));
        const clusterMarker = L.marker(center, {
          icon: L.divIcon({
            html: createClusterHtml({
              count: members.length,
              severity,
              disconnected: members.some((member) => member.status === "OFFLINE"),
              pulse: members.some((member) => member.alert),
            }),
            className: "safers-poi-icon safers-device-cluster",
            iconSize: [CLUSTER_SIZE, CLUSTER_SIZE],
            iconAnchor: [CLUSTER_SIZE / 2, CLUSTER_SIZE / 2],
          }),
          title: `장비 ${members.length}개`,
          keyboard: false,
        })
          .on("click", (event: L.LeafletMouseEvent) => {
            L.DomEvent.stopPropagation(event.originalEvent);
            const fit = map.getBoundsZoom(bounds, false, L.point(96, 96));
            const zoom = Math.min(Math.max(fit, map.getZoom() + 1), DEVICE_CLUSTER_OFF_ZOOM);
            map.flyTo(bounds.getCenter(), zoom, { duration: 0.6 });
          })
          .addTo(clusterLayer);
        const bubble = clusterMarker.getElement()?.firstElementChild;
        if (animate && revealed && !clusterKeys.has(key) && bubble)
          bubble.animate(
            [
              { transform: "scale(0.5)", opacity: 0 },
              { transform: "scale(1)", opacity: 1 },
            ],
            {
              duration: SPREAD_MS * 0.75,
              delay: gathering ? SPREAD_MS * 0.6 : 0,
              easing: SPREAD_EASE,
              fill: "backwards",
            }
          );
      }
    }
    for (const [id, entry] of markers) {
      entry.spread?.cancel();
      entry.spread = undefined;
      const center = nextGroupedAt.get(id);
      const shown = layer.hasLayer(entry.marker);
      if (center) {
        if (!shown) continue;
        if (animate && !groupedAt.has(id))
          gatherIn(map, entry, center, () => layer.removeLayer(entry.marker));
        else layer.removeLayer(entry.marker);
        continue;
      }
      if (!shown) layer.addLayer(entry.marker);
      const from = groupedAt.get(id);
      if (animate && from) spreadOut(map, entry, from);
    }
    groupedAt = nextGroupedAt;
    clusterKeys = nextKeys;
  };

  const reveal = () => {
    revealed = true;
    if (reducedMotion()) return;
    for (const { marker, pin } of markers.values())
      if (layer.hasLayer(marker)) pop(pin.button, PIN_REST, "50% 100%");
    clusterLayer.eachLayer((cluster) =>
      pop((cluster as L.Marker).getElement()?.firstElementChild, "", "50% 50%")
    );
  };

  const onZoom = () => {
    const detail = labelDetail();
    for (const { pin } of markers.values()) pin.button.dataset.detail = detail;
    regroup();
  };
  map.on("zoomend", onZoom);

  return {
    // 갱신된 장비 목록에 맞춰 핀을 고친다: 있던 핀은 바뀐 것만 고치고, 새 장비는 추가, 빠진 장비는 지운다.
    // 빠진 장비의 id 를 돌려준다 — 열려 있던 팝업이 그 장비면 닫는 데 쓴다.
    update(next: ModelDevicePoint[]): string[] {
      devices = next;
      const shown = new Set<string>();
      const detail = labelDetail();
      for (const d of next) {
        shown.add(d.deviceId);
        let entry = markers.get(d.deviceId);
        if (!entry) {
          const pin = createDevicePin(d.deviceId, onSelect, { pin: true });
          pin.button.dataset.detail = detail;
          const marker = L.marker([d.lat, d.lon], { icon: deviceIcon(pin), keyboard: false });
          entry = { marker, pin, target: { lat: d.lat, lng: d.lon } };
          markers.set(d.deviceId, entry);
        } else if (entry.target.lat !== d.lat || entry.target.lng !== d.lon) {
          entry.target = { lat: d.lat, lng: d.lon };
          slideMarker(entry, entry.target);
        }
        updateDevicePin(entry.pin, d);
      }

      const removed: string[] = [];
      for (const [id, entry] of markers) {
        if (shown.has(id)) continue;
        stopSlide(entry);
        layer.removeLayer(entry.marker);
        markers.delete(id);
        removed.push(id);
      }
      regroup();
      if (!revealed && next.length) reveal();
      return removed;
    },
    marker(id: string) {
      const entry = markers.get(id);
      return entry && { button: entry.pin.button, latLng: entry.marker.getLatLng() };
    },
    destroy() {
      map.off("zoomend", onZoom);
      for (const entry of markers.values()) stopSlide(entry);
      layer.remove();
      clusterLayer.remove();
      markers.clear();
    },
  };
}

export type DeviceMarkerLayer = ReturnType<typeof createDeviceMarkerLayer>;
